import { getSupabase } from 'shared/supabase/client';
import type { Notification } from 'types';

async function getCurrentPlatformUserId(): Promise<string | null> {
  const supabase = getSupabase();
  const { data: session } = await supabase.auth.getSession();
  if (!session.session) return null;
  const { data: profile } = await supabase
    .from('core_users')
    .select('id')
    .eq('auth_user_id', session.session.user.id)
    .is('deleted_at', null)
    .maybeSingle();
  return profile?.id ?? null;
}

function mapNotification(row: Record<string, unknown>): Notification {
  const type = (row.type as string) ?? 'info';
  const allowed = new Set([
    'info',
    'success',
    'warning',
    'assignment',
    'live',
    'request',
    'mention',
  ]);
  return {
    id: row.id as string,
    title: row.title as string,
    message: row.message as string,
    type: (allowed.has(type) ? type : 'info') as Notification['type'],
    isRead: Boolean(row.is_read),
    createdAt: row.created_at as string,
    link: (row.link as string | null) ?? undefined,
  };
}

export async function fetchMyNotifications(limit = 20): Promise<Notification[]> {
  const userId = await getCurrentPlatformUserId();
  if (!userId) return [];
  const { data, error } = await getSupabase()
    .from('core_notifications')
    .select('id, title, message, type, is_read, created_at, link')
    .eq('user_id', userId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []).map((row) => mapNotification(row as Record<string, unknown>));
}

export async function markNotificationRead(notificationId: string): Promise<void> {
  const { error } = await getSupabase()
    .from('core_notifications')
    .update({
      is_read: true,
      read_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', notificationId)
    .is('deleted_at', null);
  if (error) throw error;
}

export async function markAllNotificationsRead(): Promise<void> {
  const userId = await getCurrentPlatformUserId();
  if (!userId) return;
  const { error } = await getSupabase()
    .from('core_notifications')
    .update({
      is_read: true,
      read_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('user_id', userId)
    .eq('is_read', false)
    .is('deleted_at', null);
  if (error) throw error;
}
